import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { waLink } from "@/lib/whatsapp";

const ServiceRow = ({ s, i, open, onToggle }) => {
  const { t } = useLang();
  return (
    <div className="border-b border-sand" data-testid={`service-row-${s.id}`}>
      <button
        onClick={onToggle}
        data-testid={`service-toggle-${s.id}`}
        className="group flex w-full items-center justify-between gap-6 py-7 text-left sm:py-9"
      >
        <span className="flex items-baseline gap-5 sm:gap-8">
          <span className="font-grotesk text-xs tracking-[0.2em] text-teak sm:text-sm">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className={`font-display text-2xl font-light transition-colors duration-300 sm:text-4xl lg:text-5xl ${
              open ? "text-teak" : "text-espresso group-hover:text-teak"
            }`}
          >
            {s.name}
          </span>
        </span>
        <span
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-full border transition-all duration-300 ${
            open
              ? "rotate-45 border-teak bg-teak text-offwhite"
              : "border-espresso/20 text-espresso group-hover:border-teak group-hover:text-teak"
          }`}
        >
          <Plus className="h-4 w-4" />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
            data-testid={`service-detail-${s.id}`}
          >
            <div className="grid grid-cols-1 gap-8 pb-10 pt-1 md:grid-cols-12 md:pl-14">
              <div className="overflow-hidden rounded-sm md:col-span-5">
                <img
                  src={s.img}
                  alt={s.name}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <div className="flex flex-col justify-center md:col-span-7">
                <p className="text-base font-light leading-relaxed text-charcoal/80 sm:text-lg">
                  {s.tagline}
                </p>
                <p className="eyebrow mt-8">{t.servicesPage.included}</p>
                <ul className="mt-4 space-y-2.5">
                  {s.bullets.map((b, bi) => (
                    <li
                      key={bi}
                      className="flex items-start gap-3 text-sm font-light text-charcoal/75"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-teak" />
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href={waLink(`${t.nav.waMessage} — ${s.name}`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`service-wa-${s.id}`}
                  className="group mt-9 inline-flex w-fit items-center gap-2 border-b border-teak pb-1 text-sm font-medium text-espresso transition-colors hover:text-teak"
                >
                  {t.servicesPage.discuss}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Services() {
  const { t } = useLang();
  const [open, setOpen] = useState(0);

  return (
    <>
      <section className="pb-14 pt-36 sm:pt-44">
        <div className="wrap">
          <SectionHeading
            eyebrow={t.servicesPage.hero.eyebrow}
            title={t.servicesPage.hero.title}
            sub={t.servicesPage.hero.sub}
          />
        </div>
      </section>

      <section className="pb-24">
        <div className="wrap">
          <div className="border-t border-sand">
            {t.services.map((s, i) => (
              <ServiceRow
                key={s.id}
                s={s}
                i={i}
                open={open === i}
                onToggle={() => setOpen(open === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
